// LearnCore Frontend - 定义测验模式页面（显示术语，选择定义）

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { ttsService, TTSService } from '../services/ttsService';
import { useStudyTimeTracker } from '../hooks/useStudyTimeTracker';
import Loading from '../components/Loading';
import ImageDisplay from '../components/ImageDisplay';
import RichTextRenderer from '../components/RichTextRenderer';
import ClozeRenderer, { hasClozeNotation } from '../components/ClozeRenderer';
import type { StudySetDetails, QuizQuestion, QuizState, AdaptiveSessionState } from '../types';
import { 
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  BookOpenIcon,
  TrophyIcon,
  SpeakerWaveIcon,
  StopIcon
} from '@heroicons/react/24/outline';

export default function DefinitionQuizPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [studySetDetails, setStudySetDetails] = useState<StudySetDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [quizState, setQuizState] = useState<QuizState>({
    currentQuestionIndex: 0,
    totalQuestions: 0,
    correctAnswers: 0,
    answeredQuestions: new Set(),
    selectedAnswer: null,
    showAnswer: false,
    isCompleted: false
  });
  const [adaptiveEnabled, setAdaptiveEnabled] = useState<boolean>(false);
  const [adaptiveSession, setAdaptiveSession] = useState<AdaptiveSessionState | null>(null);
  const [, setAdaptiveCompleted] = useState<boolean>(false);
  
  const [autoAdvanceTimeout, setAutoAdvanceTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  // 学习时间追踪
  const timeTracker = useStudyTimeTracker({
    setId: id || '',
    mode: 'definitionQuiz',
    enabled: !!id && !!studySetDetails && !loading
  });

  useEffect(() => {
    if (id) {
      loadStudySet(id);
    }
    
    // 加载TTS设置
    ttsService.loadSettings();
  }, [id]);

  useEffect(() => {
    // 监听TTS状态变化
    const checkSpeaking = () => {
      setIsSpeaking(ttsService.isSpeaking());
    };

    const interval = setInterval(checkSpeaking, 100);
    return () => clearInterval(interval);
  }, []);

  // 当学习集加载完成后，决定是否启用自适应并生成/获取题目
  useEffect(() => {
    const init = async () => {
      if (!id || !studySetDetails) return;
      if (studySetDetails.set.flashcards.length === 0) return;
      try {
        const resp = await apiClient.getUserSettings();
        const enabled = !!resp.settings.adaptive?.definitionQuiz;
        setAdaptiveEnabled(enabled);
        if (enabled) {
          const start = await (apiClient as any).client.post(`/sets/${id}/session/start`, { mode: 'definitionQuiz' });
          const data = start.data as { session: AdaptiveSessionState; next: { question: QuizQuestion }; isCompleted: boolean };
          setAdaptiveSession(data.session);
          setQuestions([data.next.question]);
          setQuizState(prev => ({ ...prev, totalQuestions: studySetDetails.set.flashcards.length }));
          setAdaptiveCompleted(data.isCompleted);
        } else {
          generateQuestions();
        }
      } catch {
        generateQuestions();
      }
    };
    if (studySetDetails) init();
  }, [studySetDetails]);

  const loadStudySet = async (setId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getStudySet(setId);
      setStudySetDetails(response);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('学习集不存在');
      } else if (err.response?.status === 403) {
        setError('无权访问此学习集');
      } else {
        setError(err.response?.data?.error || '获取学习集失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateQuestions = () => {
    if (!studySetDetails) return;

    const flashcards = studySetDetails.set.flashcards;
    const questions: QuizQuestion[] = [];

    // 为每张卡片生成一个题目
    flashcards.forEach((card) => {
      // 获取其他卡片作为错误选项
      const otherCards = flashcards.filter(c => c.id !== card.id);
      
      // 随机选择3个错误答案
      const wrongOptions = [];
      const shuffledOthers = [...otherCards].sort(() => Math.random() - 0.5);
      
      for (let i = 0; i < Math.min(3, shuffledOthers.length); i++) {
        wrongOptions.push({
          id: `wrong-${i}`,
          text: shuffledOthers[i].definition,
          isCorrect: false
        });
      }

      // 正确答案
      const correctOption = {
        id: 'correct',
        text: card.definition,
        isCorrect: true
      };

      // 混合所有选项并随机排序
      const options = [correctOption, ...wrongOptions].sort(() => Math.random() - 0.5);

      questions.push({
        id: card.id,
        question: card.term,
        question_image_url: card.term_image_url,
        options,
        correctAnswerId: 'correct'
      });
    });

    setQuestions(questions);
    setQuizState(prev => ({
      ...prev,
      totalQuestions: questions.length
    }));
  };

  const handleOptionSelect = (optionId: string) => {
    if (quizState.showAnswer) return;

    // 清除之前的自动跳转定时器
    if (autoAdvanceTimeout) {
      clearTimeout(autoAdvanceTimeout);
      setAutoAdvanceTimeout(null);
    }

    const currentQuestion = questions[quizState.currentQuestionIndex];
    const selectedOption = currentQuestion.options.find(opt => opt.id === optionId);
    const isCorrect = selectedOption?.isCorrect || false;

    setQuizState(prev => ({
      ...prev,
      selectedAnswer: optionId,
      showAnswer: true
    }));
    
    // 记录学习交互（答题）
    timeTracker.recordInteraction(1);

    // 如果回答正确，0.5秒后自动跳到下一题
    if (isCorrect) {
      const timeoutId = setTimeout(() => {
        handleAutoAdvance(optionId, currentQuestion);
        setAutoAdvanceTimeout(null);
      }, 500);
      setAutoAdvanceTimeout(timeoutId);
    }
  };

  const handleAutoAdvance = (selectedAnswerId: string, currentQuestion: any) => {
    const isCorrect = currentQuestion.options.find(
      (opt: any) => opt.id === selectedAnswerId
    )?.isCorrect || false;
    if (adaptiveEnabled) {
      if (!id || !adaptiveSession) return;
      (async () => {
        try {
          const res = await (apiClient as any).client.post(`/sets/${id}/session/answer`, {
            mode: 'definitionQuiz',
            session: adaptiveSession,
            cardId: currentQuestion.id,
            selectedOptionId: selectedAnswerId
          });
          const data = res.data as { session: AdaptiveSessionState; next?: { question: QuizQuestion }; isCompleted: boolean };
          setAdaptiveSession(data.session);
          setAdaptiveCompleted(data.isCompleted);
          if (data.isCompleted) {
            setQuizState(prev => ({ ...prev, isCompleted: true }));
          } else if (data.next?.question) {
            setQuestions(q => [...q, data.next!.question]);
            setQuizState(prev => ({
              ...prev,
              correctAnswers: isCorrect ? prev.correctAnswers + 1 : prev.correctAnswers,
              answeredQuestions: new Set(prev.answeredQuestions).add(currentQuestion.id),
              currentQuestionIndex: prev.currentQuestionIndex + 1,
              selectedAnswer: null,
              showAnswer: false
            }));
          }
        } catch (err: any) {
          console.error('自适应自动前进失败:', (err && err.response?.data?.error) || (err && err.message) || err);
        }
      })();
      return;
    }

    setQuizState(prev => {
      const newAnsweredQuestions = new Set(prev.answeredQuestions);
      newAnsweredQuestions.add(currentQuestion.id);

      const nextIndex = prev.currentQuestionIndex + 1;
      const isLastQuestion = nextIndex >= questions.length;

      return {
        ...prev,
        correctAnswers: isCorrect ? prev.correctAnswers + 1 : prev.correctAnswers,
        answeredQuestions: newAnsweredQuestions,
        currentQuestionIndex: nextIndex,
        selectedAnswer: null,
        showAnswer: false,
        isCompleted: isLastQuestion
      };
    });
  };

  const handleContinue = async () => {
    if (!quizState.selectedAnswer) return;

    const currentQuestion = questions[quizState.currentQuestionIndex];
    const isCorrect = currentQuestion.options.find(
      opt => opt.id === quizState.selectedAnswer
    )?.isCorrect || false;
    if (adaptiveEnabled) {
      if (!id || !adaptiveSession) return;
      try {
        const res = await (apiClient as any).client.post(`/sets/${id}/session/answer`, {
          mode: 'definitionQuiz',
          session: adaptiveSession,
          cardId: currentQuestion.id,
          selectedOptionId: quizState.selectedAnswer
        });
        const data = res.data as { session: AdaptiveSessionState; next?: { question: QuizQuestion }; isCompleted: boolean };
        setAdaptiveSession(data.session);
        setAdaptiveCompleted(data.isCompleted);
        if (data.isCompleted) {
          setQuizState(prev => ({ ...prev, isCompleted: true }));
        } else if (data.next?.question) {
          setQuestions(q => [...q, data.next!.question]);
          setQuizState(prev => ({
            ...prev,
            correctAnswers: isCorrect ? prev.correctAnswers + 1 : prev.correctAnswers,
            answeredQuestions: new Set(prev.answeredQuestions).add(currentQuestion.id),
            currentQuestionIndex: prev.currentQuestionIndex + 1,
            selectedAnswer: null,
            showAnswer: false
          }));
        }
      } catch (err: any) {
        console.error('自适应答题失败:', (err && err.response?.data?.error) || (err && err.message) || err);
      }
      return;
    }

    const newAnsweredQuestions = new Set(quizState.answeredQuestions);
    newAnsweredQuestions.add(currentQuestion.id);

    const nextIndex = quizState.currentQuestionIndex + 1;
    const isLastQuestion = nextIndex >= questions.length;

    setQuizState(prev => ({
      ...prev,
      correctAnswers: isCorrect ? prev.correctAnswers + 1 : prev.correctAnswers,
      answeredQuestions: newAnsweredQuestions,
      currentQuestionIndex: nextIndex,
      selectedAnswer: null,
      showAnswer: false,
      isCompleted: isLastQuestion
    }));
  };

  const handleSpeak = async (text: string) => {
    if (isSpeaking) {
      ttsService.stop();
      return;
    }

    try {
      await ttsService.speak(text);
    } catch (error) {
      console.error('TTS播放失败:', error);
    }
  };

  const resetQuiz = () => {
    // 清除自动跳转定时器
    if (autoAdvanceTimeout) {
      clearTimeout(autoAdvanceTimeout);
      setAutoAdvanceTimeout(null);
    }
    
    setQuizState({
      currentQuestionIndex: 0,
      totalQuestions: questions.length,
      correctAnswers: 0,
      answeredQuestions: new Set(),
      selectedAnswer: null,
      showAnswer: false,
      isCompleted: false
    });
    generateQuestions();
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载学习集..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <BookOpenIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {error}
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="btn-primary"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  if (!studySetDetails || studySetDetails.set.flashcards.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <BookOpenIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            此学习集没有卡片
          </h1>
          <Link
            to={`/sets/${id}/view`}
            className="btn-primary"
          >
            查看学习集
          </Link>
        </div>
      </div>
    );
  }

  if (studySetDetails.set.flashcards.length < 4) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <BookOpenIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            测验模式需要至少4张卡片
          </h1>
          <p className="text-gray-600 mb-6">
            当前学习集只有 {studySetDetails.set.flashcards.length} 张卡片，无法生成足够的选项进行测验。
          </p>
          <Link
            to={`/sets/${id}/view`}
            className="btn-primary"
          >
            查看学习集
          </Link>
        </div>
      </div>
    );
  }

  const { set } = studySetDetails;
  const currentQuestion = questions[quizState.currentQuestionIndex];
  const linearProgress = ((quizState.currentQuestionIndex + 1) / questions.length) * 100;
  const total = set.flashcards.length;
  const unseenCount = adaptiveEnabled ? (adaptiveSession?.unseen_queue.length ?? 0) : 0;
  const learningCount = adaptiveEnabled ? (adaptiveSession?.learning_bucket.length ?? 0) : 0;
  const mastered = adaptiveEnabled ? Math.max(0, total - (unseenCount + learningCount)) : 0;
  const masteredPercent = adaptiveEnabled && total > 0 ? Math.round((mastered / total) * 100) : 0;
  const selectedOption = quizState.selectedAnswer ? 
    currentQuestion?.options.find(opt => opt.id === quizState.selectedAnswer) : null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <Link
            to={`/sets/${id}/view`}
            state={{ from: location.pathname }}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            返回查看页面
          </Link>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">自适应</span>
              <button
                type="button"
                onClick={async () => {
                  if (!id || !studySetDetails) return;
                  const target = !adaptiveEnabled;
                  setAdaptiveEnabled(target);
                  if (target) {
                    try {
                      const start = await (apiClient as any).client.post(`/sets/${id}/session/start`, { mode: 'definitionQuiz' });
                      const data = start.data as { session: AdaptiveSessionState; next: { question: QuizQuestion }; isCompleted: boolean };
                      setAdaptiveSession(data.session);
                      setQuestions([data.next.question]);
                      setQuizState(prev => ({ ...prev, currentQuestionIndex: 0, totalQuestions: studySetDetails.set.flashcards.length, correctAnswers: 0, answeredQuestions: new Set(), selectedAnswer: null, showAnswer: false, isCompleted: data.isCompleted }));
                    } catch {
                      setAdaptiveEnabled(false);
                    }
                  } else {
                    setAdaptiveSession(null);
                    setQuizState(prev => ({ ...prev, currentQuestionIndex: 0, totalQuestions: 0, correctAnswers: 0, answeredQuestions: new Set(), selectedAnswer: null, showAnswer: false, isCompleted: false }));
                    generateQuestions();
                  }
                }}
                className={`${adaptiveEnabled ? 'bg-primary-600 focus:ring-primary-500' : 'bg-gray-200 focus:ring-gray-500'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2`}
              >
                <span
                  className={`${adaptiveEnabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-300 shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <button
              onClick={resetQuiz}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <i className="fas fa-redo h-4 w-4 mr-1"></i>
              重新开始
            </button>
          </div>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          定义测验 - {set.title}
        </h1>
        <p className="text-gray-600 mb-4">
          根据术语选择正确的定义
        </p>
        
        {/* 进度条 */}
        {!quizState.isCompleted && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              {adaptiveEnabled ? (
                <span>已掌握 {mastered} ・ 学习中 {learningCount} ・ 总 {total}</span>
              ) : (
                <span>进度: {quizState.currentQuestionIndex + 1} / {questions.length}</span>
              )}
              <span>{adaptiveEnabled ? masteredPercent : Math.round(linearProgress)}% 完成</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${adaptiveEnabled ? masteredPercent : Math.round(linearProgress)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* 测验完成页面 */}
      {quizState.isCompleted ? (
        <div className="text-center py-12">
          <TrophyIcon className="h-20 w-20 text-yellow-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            测验完成！
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            您的成绩: {quizState.correctAnswers} / {questions.length} 
            ({Math.round((quizState.correctAnswers / questions.length) * 100)}%)
          </p>
          
          <div className="bg-gray-50 rounded-lg p-6 mb-8 max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">测验统计</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-green-600">正确答案:</span>
                <span className="font-medium">{quizState.correctAnswers} 题</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-600">错误答案:</span>
                <span className="font-medium">{questions.length - quizState.correctAnswers} 题</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-900 font-medium">总题数:</span>
                <span className="font-bold">{questions.length} 题</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={resetQuiz}
              className="btn-outline"
            >
              <i className="fas fa-redo h-4 w-4 mr-2"></i>
              重新测验
            </button>
            
            <Link
              to={`/sets/${id}/view`}
              className="btn-primary"
            >
              <BookOpenIcon className="h-4 w-4 mr-2" />
              查看学习集
            </Link>
          </div>
        </div>
      ) : (
        currentQuestion && (
          <>
            {/* 题目 */}
            <div className="mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-700 p-8 mb-6 relative">
                <h2 className="text-sm font-medium text-gray-500 mb-2">术语</h2>
                
                {/* 显示题目图片（如果有） */}
                {currentQuestion.question_image_url && (
                  <div className="mb-4 flex justify-center">
                    <ImageDisplay 
                      imageUrl={currentQuestion.question_image_url} 
                      alt="题目图片"
                      size="md"
                    />
                  </div>
                )}
                
                <div className="text-xl text-gray-900 leading-relaxed pr-12">
                  <ClozeRenderer 
                    content={currentQuestion.question} 
                    showCloze={hasClozeNotation(currentQuestion.question)}
                  />
                </div>
                
                {/* TTS按钮 */}
                {TTSService.isSupported() && (
                  <button
                    onClick={() => handleSpeak(currentQuestion.question)}
                    className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
                      isSpeaking 
                        ? 'bg-danger-100 text-danger-600 hover:bg-danger-200' 
                        : 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                    }`}
                    title={isSpeaking ? '停止朗读' : '朗读题目'}
                  >
                    {isSpeaking ? (
                      <StopIcon className="h-5 w-5" />
                    ) : (
                      <SpeakerWaveIcon className="h-5 w-5" />
                    )}
                  </button>
                )}
              </div>

              {/* 选项 */}
              <div className="grid gap-3">
                {currentQuestion.options.map((option, index) => {
                  const isSelected = quizState.selectedAnswer === option.id;
                  const isCorrect = option.isCorrect;
                  const showCorrect = quizState.showAnswer && isCorrect;
                  const showWrong = quizState.showAnswer && isSelected && !isCorrect;
                  
                  return (
                    <div
                      key={option.id}
                      onClick={() => !quizState.showAnswer && handleOptionSelect(option.id)}
                      className={`
                        flex items-center justify-between p-4 rounded-lg border-2 text-left transition-all
                        ${!quizState.showAnswer && 'hover:bg-gray-50 cursor-pointer'}
                        ${isSelected && !quizState.showAnswer && 'border-primary-500 bg-primary-50'}
                        ${!isSelected && !quizState.showAnswer && 'border-gray-200'}
                        ${showCorrect && 'border-green-500 bg-green-50 border-dashed'}
                        ${showWrong && 'border-red-500 bg-red-50'}
                        ${quizState.showAnswer && !isSelected && !isCorrect && 'border-gray-200 bg-gray-50 opacity-60'}
                        ${quizState.showAnswer && 'cursor-default'}
                      `}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`
                          w-6 h-6 rounded-full border-2 flex items-center justify-center text-sm font-medium
                          ${!quizState.showAnswer && isSelected && 'border-primary-500 bg-primary-500 text-white'}
                          ${!quizState.showAnswer && !isSelected && 'border-gray-300'}
                          ${showCorrect && 'border-green-500 bg-green-500 text-white'}
                          ${showWrong && 'border-red-500 bg-red-500 text-white'}
                          ${quizState.showAnswer && !isSelected && !isCorrect && 'border-gray-300'}
                        `}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <div className="flex-1">
                          <div className="text-gray-900">
                            <RichTextRenderer content={option.text} />
                          </div>
                          {/* 错误时显示对应信息 */}
                          {quizState.showAnswer && selectedOption && !selectedOption.isCorrect && (() => {
                            const relatedCard = studySetDetails?.set.flashcards.find(card => 
                              card.definition === option.text
                            );
                            return relatedCard ? (
                              <div className="text-sm text-gray-600 mt-1">
                                → {relatedCard.term}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {/* TTS按钮 */}
                        {TTSService.isSupported() && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSpeak(option.text);
                            }}
                            className={`p-1.5 rounded-full transition-colors ${
                              isSpeaking 
                                ? 'bg-danger-100 text-danger-600 hover:bg-danger-200' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title={isSpeaking ? '停止朗读' : '朗读选项'}
                          >
                            {isSpeaking ? (
                              <StopIcon className="h-4 w-4" />
                            ) : (
                              <SpeakerWaveIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        
                        {/* 状态图标 */}
                        {quizState.showAnswer && (
                          <>
                            {isCorrect && (
                              <CheckIcon className="h-5 w-5 text-green-600" />
                            )}
                            {showWrong && (
                              <XMarkIcon className="h-5 w-5 text-red-600" />
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>


            {/* 控制按钮和反馈信息 */}
            <div className="flex items-center justify-between mt-6">
              {/* 左侧占位 */}
              <div className="w-32"></div>
              
              {/* 中间反馈信息 */}
              <div className="flex-1 flex justify-center">
                {quizState.showAnswer && selectedOption ? (
                  selectedOption.isCorrect ? (
                    <div className="inline-flex items-center px-4 py-2 rounded-lg bg-green-100 text-green-800">
                      <CheckIcon className="h-5 w-5 mr-2" />
                      正确！{quizState.currentQuestionIndex < questions.length - 1 && (
                        <span className="ml-2 text-sm">自动进入下一题...</span>
                      )}
                    </div>
                  ) : (
                    <div className="inline-flex items-center px-4 py-2 rounded-lg bg-red-100 text-red-800">
                      <XMarkIcon className="h-5 w-5 mr-2" />
                      错误，正确答案是 "{currentQuestion.options.find(opt => opt.isCorrect)?.text}"
                    </div>
                  )
                ) : !quizState.showAnswer ? (
                  <p className="text-gray-500 text-sm">
                    请选择一个答案
                  </p>
                ) : null}
              </div>
              
              {/* 右侧控制按钮 */}
              <div className="w-32 flex justify-end">
                {quizState.showAnswer && selectedOption && !selectedOption.isCorrect && (
                  <button
                    onClick={handleContinue}
                    className="btn-primary px-4 py-2 whitespace-nowrap"
                  >
                    {adaptiveEnabled ? (quizState.isCompleted ? '查看结果' : '继续') : (quizState.currentQuestionIndex === questions.length - 1 ? '查看结果' : '继续')}
                    <ArrowLeftIcon className="h-4 w-4 ml-2 rotate-180" />
                  </button>
                )}
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}
